import { useState } from 'react';
import * as Sentry from '@sentry/react';
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Loader2,
  MoreVertical,
  Plus,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DeleteConfirmationModal } from '@/components/delete-confirmation-modal';
import { PageTitle } from '@/components/page-title';
import { PageDescription } from '@/components/page-description';
import { useNotification } from '@/components/notifications';
import { useOrgStore } from '@/stores/org-store';
import { useListOrgMcpTools } from '@/hooks/queries/org-mcp-tools/use-list-org-mcp-tools';
import {
  useDeleteOrgMcpTool,
  useRefreshOrgMcpTool,
  useToggleOrgMcpTool,
  useUpdateOrgMcpTool,
} from '@/hooks/mutations/org-mcp-tools/use-org-mcp-tool-mutations';
import { OrgMcpTool } from '@/services/org-mcp-tools/types';
import { AddEditToolModal } from './add-edit-tool-modal';

export const MyToolsPage = () => {
  const notification = useNotification();
  const { currentOrg } = useOrgStore();
  const orgId = currentOrg?.orgid;
  const { data: tools = [], isLoading } = useListOrgMcpTools(orgId);
  const toggle = useToggleOrgMcpTool(orgId);
  const update = useUpdateOrgMcpTool(orgId);
  const refresh = useRefreshOrgMcpTool(orgId);
  const remove = useDeleteOrgMcpTool(orgId);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<OrgMcpTool | null>(null);
  const [deleting, setDeleting] = useState<OrgMcpTool | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const toggleExpanded = (id: string) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // A tool is exposed when the whole server is (null/empty selected_tools ==
  // "expose all", matching the backend's `selected_tools or None`) or when its
  // name is in the explicit subset.
  const toolExposed = (tool: OrgMcpTool, name: string) =>
    !tool.selected_tools?.length || tool.selected_tools.includes(name);

  const handleToolToggle = async (
    tool: OrgMcpTool,
    name: string,
    checked: boolean,
  ) => {
    const universe = (tool.available_tools ?? []).map((t) => t.name);
    // Materialize the current exposed set against the known universe (null =
    // all), then add/remove the toggled tool.
    const current = !tool.selected_tools?.length
      ? universe
      : tool.selected_tools.filter((n) => universe.includes(n));
    const next = checked
      ? Array.from(new Set([...current, name]))
      : current.filter((n) => n !== name);

    // Can't expose none: an empty allowlist collapses to "expose all" on the
    // backend, so removing the last tool is blocked — disable the server for
    // that (matches the modal's invariant).
    if (next.length === 0) {
      notification.error(
        'At least one tool must stay exposed — disable the server instead.',
      );
      return;
    }
    // Every tool selected -> send null (expose all) to match backend semantics.
    const selected_tools = next.length === universe.length ? null : next;
    try {
      await update.mutateAsync({ toolId: tool.id, input: { selected_tools } });
      notification.success(checked ? `Enabled ${name}` : `Disabled ${name}`);
    } catch (error) {
      Sentry.captureException(error, {
        extra: { context: 'org-mcp-tool-select' },
      });
      notification.error(
        error instanceof Error ? error.message : 'Failed to update tool',
      );
    }
  };

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (tool: OrgMcpTool) => {
    setEditing(tool);
    setModalOpen(true);
  };

  const handleToggle = async (tool: OrgMcpTool, enabled: boolean) => {
    try {
      await toggle.mutateAsync({ toolId: tool.id, enabled });
      notification.success(enabled ? 'Tool enabled' : 'Tool disabled');
    } catch (error) {
      notification.error(
        error instanceof Error ? error.message : 'Failed to update tool',
      );
    }
  };

  const handleRefresh = async (tool: OrgMcpTool) => {
    try {
      const res = await refresh.mutateAsync(tool.id);
      if (!res.ok) {
        // Defensive: a failed handshake normally throws (HTTP 400); this guards
        // the { ok: false } fallback so a failure isn't reported as success.
        notification.error(
          'Could not refresh tools — the connection test did not succeed.',
        );
        return;
      }
      notification.success(`Refreshed — ${res.tools.length} tool(s) found`);
      // Auto-expand so the freshly-fetched tools are immediately visible.
      setExpandedIds((prev) => new Set(prev).add(tool.id));
    } catch (error) {
      Sentry.captureException(error, {
        extra: { context: 'org-mcp-tool-refresh' },
      });
      notification.error(
        error instanceof Error ? error.message : 'Failed to refresh tools',
      );
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await remove.mutateAsync(deleting.id);
      notification.success('Tool deleted');
      setDeleting(null);
    } catch (error) {
      notification.error(
        error instanceof Error ? error.message : 'Failed to delete tool',
      );
    }
  };

  return (
    <div className='w-full'>
      <div className='mb-6 flex items-start justify-between gap-4'>
        <div>
          <PageTitle title='My Tools' />
          <PageDescription>
            Add third-party MCP tools for your organization. They become
            selectable nodes in Workflow Builder and callable by the Chat Agent.
          </PageDescription>
        </div>
        <Button onClick={openCreate} className='shrink-0'>
          <Plus className='size-4' /> Add tool
        </Button>
      </div>

      {isLoading ? (
        <div className='flex items-center justify-center py-10'>
          <Loader2 className='size-5 animate-spin text-muted-foreground' />
          <span className='ml-2 text-muted-foreground'>Loading tools...</span>
        </div>
      ) : tools.length === 0 ? (
        <div className='rounded-md border border-dashed py-10 text-center text-sm text-muted-foreground'>
          No tools yet. Click “Add tool” to connect an MCP server.
        </div>
      ) : (
        <ul className='divide-y rounded-md border'>
          {tools.map((tool) => (
            <li key={tool.id} className='p-4'>
              <div className='flex items-center justify-between gap-4'>
                <div className='min-w-0'>
                  <div className='flex items-center gap-2'>
                    {tool.available_tools?.length ? (
                      <button
                        type='button'
                        onClick={() => toggleExpanded(tool.id)}
                        className='shrink-0 text-muted-foreground hover:text-foreground'
                        aria-label={
                          expandedIds.has(tool.id)
                            ? `Collapse ${tool.name} tools`
                            : `Expand ${tool.name} tools`
                        }
                        aria-expanded={expandedIds.has(tool.id)}
                      >
                        {expandedIds.has(tool.id) ? (
                          <ChevronDown className='size-4' />
                        ) : (
                          <ChevronRight className='size-4' />
                        )}
                      </button>
                    ) : null}
                    <span className='truncate font-medium'>{tool.name}</span>
                    {tool.last_validation_status === 'ok' ? (
                      <CheckCircle2 className='size-4 shrink-0 text-green-600' />
                    ) : tool.last_validation_status === 'failed' ? (
                      <AlertCircle className='size-4 shrink-0 text-red-600' />
                    ) : null}
                    {tool.tags?.map((tag) => (
                      <Badge key={tag} variant='secondary'>
                        {tag}
                      </Badge>
                    ))}
                  </div>
                  {tool.description ? (
                    <p className='truncate text-sm text-muted-foreground'>
                      {tool.description}
                    </p>
                  ) : null}
                  <p className='truncate text-xs text-muted-foreground'>
                    {tool.server_url}
                  </p>
                  <p className='truncate text-xs text-muted-foreground'>
                    {tool.selected_tools?.length
                      ? `${tool.selected_tools.length} tool${
                          tool.selected_tools.length === 1 ? '' : 's'
                        }: ${tool.selected_tools.join(', ')}`
                      : 'All tools exposed'}
                  </p>
                  {tool.last_validation_status === 'failed' &&
                  tool.last_validation_error ? (
                    <p className='truncate text-xs text-red-600'>
                      {tool.last_validation_error}
                    </p>
                  ) : null}
                </div>

                <div className='flex items-center gap-3'>
                  <Switch
                    checked={tool.enabled}
                    disabled={toggle.isPending}
                    onCheckedChange={(checked) => handleToggle(tool, checked)}
                    aria-label={`Enable ${tool.name}`}
                  />
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant='ghost'
                        size='icon'
                        aria-label='Tool actions'
                      >
                        <MoreVertical className='size-4' />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align='end'>
                      <DropdownMenuItem onClick={() => openEdit(tool)}>
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openEdit(tool)}>
                        Re-authenticate
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleRefresh(tool)}
                        disabled={refresh.isPending}
                      >
                        Refresh tools
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => setDeleting(tool)}>
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              {expandedIds.has(tool.id) && tool.available_tools?.length ? (
                <div className='mt-3 space-y-1 rounded-md border bg-muted/30 p-2'>
                  {tool.available_tools.map((t) => (
                    <label
                      key={t.name}
                      className='flex items-start justify-between gap-3 rounded px-2 py-1.5 text-sm hover:bg-muted/50'
                    >
                      <span className='min-w-0'>
                        <span className='block truncate font-medium'>
                          {t.name}
                        </span>
                        {t.description ? (
                          <span className='block truncate text-xs text-muted-foreground'>
                            {t.description}
                          </span>
                        ) : null}
                      </span>
                      <Switch
                        className='mt-0.5 shrink-0'
                        checked={toolExposed(tool, t.name)}
                        disabled={!tool.enabled || update.isPending}
                        onCheckedChange={(c) =>
                          handleToolToggle(tool, t.name, c)
                        }
                        aria-label={`Expose ${t.name}`}
                      />
                    </label>
                  ))}
                  {!tool.enabled ? (
                    <p className='px-2 pt-1 text-xs text-muted-foreground'>
                      Enable the server to change which tools are exposed.
                    </p>
                  ) : null}
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {orgId && (
        <AddEditToolModal
          orgId={orgId}
          tool={editing}
          isOpen={modalOpen}
          onOpenChange={setModalOpen}
        />
      )}

      <DeleteConfirmationModal
        isOpen={!!deleting}
        title='Delete tool'
        description={`Delete “${deleting?.name}”? This removes it from the node picker and the Chat Agent, and deletes its stored credentials. This cannot be undone.`}
        buttonLabel='Delete'
        isLoading={remove.isPending}
        handleDelete={handleDelete}
        handleCancel={() => setDeleting(null)}
      />
    </div>
  );
};
